import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import ProductMatching from "../../product/component/productMatching/ProductMatching";
import LoadingOverlay from "../productList/components/LoadingOverlay";
import ProductEditInfoCard from "./components/ProductEditInfoCard";
import ProductEditUnitFormCard from "./components/ProductEditUnitFormCard";
import { useProductEditViewModel } from "./hooks/useProductEditViewModel";
import { ProductEditModalProps } from "./types/productEditView.types";

/**
 * Tailwind rebuild of the legacy `ProductEditModal`
 * (src/screens/product/productEdit/ProductEditModal.tsx), following the
 * same shell as `ProductRegisterModal` — `TailwindSideModal` +
 * `CommonSplitContainer` + `TailwindBottomButtonView`. All state/data-
 * fetching lives in `useProductEditViewModel`; this component only composes
 * presentation.
 *
 * Drop-in replacement for the legacy modal wherever it's rendered (identical
 * props) — used from `ProductListNew.tsx`. `ProductMatching` (rendered only
 * for average products) is reused untouched from the legacy screen — it
 * already renders mostly-Tailwind markup of its own and is out of scope for
 * this pass.
 */
const ProductEditModal = ({ visibleModal, setVisibleModal, productId }: ProductEditModalProps) => {
  const vm = useProductEditViewModel({ visibleModal, setVisibleModal, productId });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onCancel();
        }}
        title="Edit product"
        titleClassName="tw-font-display"
      >
        <CommonSplitContainer
          currentHeight={0.9}
          overflowX="hidden"
          topChild={
            <div className="tw-flex tw-flex-col tw-gap-6 tw-bg-muted/20 tw-p-6">
              <ProductEditInfoCard vm={vm} />
              <ProductEditUnitFormCard vm={vm} />
              {vm.showProductMatching ? <ProductMatching id={vm.productDetails?._id} /> : null}
            </div>
          }
          bottomChild={
            <TailwindBottomButtonView
              cancelButtonStatus
              saveButtonStatus
              saveTitle={vm.saveTitle}
              buttonSize="lg"
              cancelButton={vm.onCancel}
              saveButton={vm.onSave}
            />
          }
        />
      </TailwindSideModal>

      <ConfirmModal
        viewModal={vm.visibleDeleteModal}
        closeModal={vm.onCloseDeleteModal}
        cancel={vm.onCancelDeleteModal}
        approve={vm.onConfirmDeleteUnit}
        title="Delete Unit Size"
        subTitle="Are you sure you want to delete unit size?"
      />

      <ConfirmModal
        viewModal={vm.visibleAverageConfirmModal}
        closeModal={vm.onCancelAverageChange}
        cancel={vm.onCancelAverageChange}
        approve={vm.onConfirmAverageChange}
        title="Change Average Impact Product Status"
        subTitle={vm.averageConfirmMessage}
      />

      {vm.isSaving ? <LoadingOverlay /> : null}
    </>
  );
};

export default ProductEditModal;
